import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
// The header's slide-in and the dropdown fade are CSS keyframes (index.css:
// .tda-nav-enter / .tda-dropdown-enter) instead of framer-motion, which keeps
// the ~100 KB (compressed) library out of the entry bundle on every page.
import { Button } from "@/components/ui/button";
import { Phone, Menu, X, MapPin, Clock, ChevronDown, MessageSquare, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
// Both of these render only in the lg+ bar (hidden on phones), so their code is
// fetched on demand instead of being parsed by every mobile visitor.
const DentalAISearch = lazy(() => import('@/components/DentalAISearch'));
import { isMarketingLandingPath } from '@/lib/marketingConsent';
import OptimizedImage from '@/components/OptimizedImage';
import { base44 } from '@/api/base44Client';
const RecentlyViewed = lazy(() => import('@/components/navigation/RecentlyViewed'));
// 672×168 transparent WebP (33 KB) cut from the content band of the original
// 1024×1024 PNG (85 KB), which the header was already centre-cropping to that
// band. Bundled so it ships with the hashed-asset cache policy.
import tdaLogo from '@/assets/brand/tda-logo.webp';
import tdaLogo224 from '@/assets/brand/tda-logo-224.webp';
import tdaLogo448 from '@/assets/brand/tda-logo-448.webp';

const navLinks = [
{ label: "Home", href: "/" },
{
  label: "Resources",
  href: "/resources",
  subLinks: [
  { label: "About", href: "/about" },
  { label: "Meet the Doctor", href: "/meet-the-doctor" },
  { label: "Patient Education", href: "/patient-education" },
  { label: "Our Results", href: "/our-results" },
  { label: "Our Technology", href: "/our-technology" },
  { label: "In-House Lab", href: "/in-house-lab" },
  { label: "Financing Options", href: "/financing-options" },
  { label: "Dental Insurance", href: "/resources/dental-insurance" },
  { label: "Office Policies", href: "/resources/office-policies" },
  { label: "Dental Assessment", href: "/dental-assessment" },
  { label: "Blog", href: "/blog" },
  { label: "FAQ", href: "/faq" }]

},
{
  label: "Dental Implants",
  href: "/dental-implants",
  subLinks: [
  { label: "Single Implant", href: "/single-implant" },
  { label: "Implant Cost & Pricing", href: "/dental-implant-cost" },
  { label: "Full Mouth Reconstruction", href: "/full-mouth-restoration" },
  { label: "Fixed New Teeth", href: "/fixed-new-teeth" },
  { label: "Snap-in vs Fixed Implant Teeth", href: "/snap-in-dentures" },
  { label: "Mini Dental Implants", href: "/mini-dental-implants" },
  { label: "Sinus Grafts", href: "/sinus-grafts" },
  { label: "Bone Grafting", href: "/bone-grafting" },
  { label: "Regenerative Dentistry", href: "/regenerative-dentistry" },
  { label: "Dental Implants FAQ", href: "/dental-implants-faq" },
  { label: "Dental Implants Guide", href: "/dental-implants-guide" }]

},
{
  label: "Services",
  href: "/services",
  dataTour: "services",
  subLinks: [
  { label: "Cosmetic Dentistry", href: "/cosmetic-dentistry" },
  { label: "General Dentistry", href: "/general-dentistry" },
  { label: "Invisalign", href: "/invisalign" },
  { label: "Same-Day Crowns", href: "/same-day-crowns" },
  { label: "Emergency Dentistry", href: "/emergency-dentistry" },
  { label: "Laser Dentistry", href: "/laser-dentistry" },
  { label: "Oral Surgery", href: "/oral-surgery" },
  { label: "Tooth Extractions", href: "/tooth-extractions" },
  { label: "Sedation Dentistry", href: "/sedation-dentistry" },
  { label: "Periodontal Disease Treatment", href: "/periodontal-disease-treatment" },
  { label: "Dental Bridges", href: "/dental-bridges" },
  { label: "Dentures", href: "/dentures" },
  { label: "Preventative Dentistry", href: "/preventative-dentistry" },
  { label: "Smile Visualizer", href: "/smile-visualizer" }]

},
{ label: "Reviews", href: "/patient-reviews", dataTour: "reviews" },
{ label: "Contact", href: "/contact-us", dataTour: "contact" }];


export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null); // which mobile accordion section is open
  const scrollRef = useRef(null);

  const closeMobile = () => {setMobileMenuOpen(false);setMobileExpanded(null);};

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {document.body.style.overflow = '';};
  }, [mobileMenuOpen]);



  // Close dropdown on Escape key
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && openDropdown) {
        setOpenDropdown(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openDropdown]);

  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setScrolled(window.scrollY > 20);
      }, 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  return (
    <>
      {/* Skip to main content lives in Layout so every page gets exactly one,
          including pages that render without this navbar. */}

      {/* Single sticky container — consolidates both bars to prevent compositor/paint bug */}
      <div className="sticky top-0 z-50">
        {/* Top Bar */}
        <div className="bg-slate-900 text-white py-2 hidden md:block">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-6">
                <a
                  href="https://maps.app.goo.gl/dnWeZn2SyEMD1BQ48"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-amber-400 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400"
                  onClick={() => base44.analytics.track({
                    eventName: 'get_directions_clicked',
                    properties: { location: 'navbar_top_bar' }
                  })}>
                  
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                  <span>11105 Trinity Blvd, Trinity, FL 34655</span>
                </a>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" aria-hidden="true" />
                  <span>Mon-Thu: 9am - 5pm</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <a href="tel:+17272286846" aria-label="Call (727) 228-6846" className="flex items-center gap-2 hover:text-amber-400 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400">
                  <Phone className="w-4 h-4" aria-hidden="true" />
                  (727) 228-6846
                </a>
                


                
              </div>
            </div>
          </div>
        </div>

        {/* Main Navbar */}
        <nav
          className={`tda-nav-enter transition-all duration-300 motion-reduce:animate-none ${
          scrolled ?
          'bg-white shadow-lg' :
          'bg-white'}`
          }
          role="navigation"
          aria-label="Main navigation">
          
          <div className="container mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-between h-20">
              {/* Logo */}
              <Link to="/" aria-label="Trinity Dental Arts Home">
                <OptimizedImage
                  src={tdaLogo}
                  srcSet={`${tdaLogo224} 224w, ${tdaLogo448} 448w, ${tdaLogo} 672w`}
                  alt="Trinity Dental Arts Logo"
                  className="h-14 w-56 object-contain"
                  width={224}
                  height={56}
                  objectFit="contain"
                  priority={true}
                  quality="medium"
                  sizes="(max-width: 768px) 200px, 224px" />
                
              </Link>



              {/* Desktop Navigation */}
              <ul role="menubar" className="hidden lg:flex items-center gap-8 list-none m-0 p-0">
                {navLinks.map((link) =>
                link.subLinks ?
                <li
                  key={link.label}
                  className="relative"
                  role="none"
                  onMouseEnter={() => setOpenDropdown(link.label)}
                  onMouseLeave={(event) => {
                    if (!event.currentTarget.querySelector('.tda-dropdown-enter')?.contains(document.activeElement)) {
                      setOpenDropdown(null);
                    }
                  }}
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setOpenDropdown(null);
                    }
                  }}
                  data-tour={link.dataTour}>
                  <Link
                    to={link.href}
                    className="text-slate-700 hover:text-amber-600 transition-colors text-sm font-medium flex items-center gap-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
                    role="menuitem"
                    aria-haspopup="true"
                    aria-expanded={openDropdown === link.label}
                    onFocus={() => setOpenDropdown(link.label)}
                    onClick={() => setOpenDropdown(null)}>
                    {link.label}
                    <ChevronDown className="w-4 h-4" aria-hidden="true" />
                  </Link>
                  {openDropdown === link.label && (
                    // The wrapper's padding keeps the pointer inside the menu while crossing the gap.
                    <div className="absolute top-full left-0 pt-2">
                      <div className="tda-dropdown-enter max-h-[calc(100dvh-8rem)] min-w-[200px] overflow-y-auto rounded-xl border border-slate-100 bg-white py-2 shadow-xl">
                        {link.subLinks.map((subLink) => (
                          <Link
                            key={subLink.label}
                            to={subLink.href}
                            role="menuitem"
                            onClick={() => setOpenDropdown(null)}
                            className="block px-4 py-2 text-sm text-slate-700 hover:text-amber-600 hover:bg-amber-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600">
                            {subLink.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </li> :
                link.href.startsWith('#') ?
                <li key={link.label} role="none">
                      <a
                    href={link.href}
                    role="menuitem"
                    className="text-slate-700 hover:text-amber-600 transition-colors text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
                    data-tour={link.dataTour}>
                    
                        {link.label}
                      </a>
                    </li> :

                <li key={link.label} role="none">
                      <Link
                    to={link.href}
                    role="menuitem"
                    className="text-slate-700 hover:text-amber-600 transition-colors text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
                    data-tour={link.dataTour}>
                    
                        {link.label}
                      </Link>
                    </li>

                )}
              </ul>

              {/* Search & CTA */}
              <div className="hidden lg:flex items-center gap-4">
                <div className="w-48">
                  {isMarketingLandingPath(window.location.pathname) ?
                  <a href="/patient-education#ask-a-question" className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-amber-600 hover:text-amber-700">
                      Ask a dental question
                    </a> :
                  <Suspense fallback={null}>
                    <DentalAISearch />
                  </Suspense>}
                </div>
                <Suspense fallback={null}>
                  <RecentlyViewed />
                </Suspense>
                <Button
                  data-tour="book-appointment"
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                  onClick={() => {
                    base44.analytics.track({
                      eventName: 'book_appointment_clicked',
                      properties: { location: 'navbar', button_text: 'Book Appointment' }
                    });
                    window.open('/book-appointment', '_blank');
                  }}
                  aria-label="Book appointment online">
                  
                  Book Appointment
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2.5 -mr-2 rounded-lg hover:bg-slate-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}>
                
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile drawer — rendered via portal so it sits outside sticky/transform stacking contexts */}
      {mobileMenuOpen && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff'
          }}>
          
          {/* Top bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px',
            borderBottom: '1px solid #f1f5f9',
            flexShrink: 0,
            backgroundColor: '#0f172a'
          }}>
            <Link to="/" onClick={closeMobile}>
              <OptimizedImage
                src={tdaLogo}
                alt="Trinity Dental Arts"
                width={140}
                height={48}
                style={{ height: 48, width: 140, objectFit: 'contain', objectPosition: 'center' }}
                priority={false} />
              
            </Link>
            <button
              onClick={closeMobile}
              aria-label="Close menu"
              style={{
                padding: 8,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              
              <X style={{ width: 22, height: 22, color: '#fff' }} />
            </button>
          </div>

          {/* Quick action row */}
          <div style={{
            display: 'flex',
            gap: 0,
            borderBottom: '1px solid #f1f5f9',
            flexShrink: 0
          }}>
            <a
              href="tel:+17272286846"
              onClick={closeMobile}
              aria-label="Call (727) 228-6846"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '14px 8px',
                gap: 4,
                background: '#f8fafc',
                borderRight: '1px solid #e2e8f0',
                textDecoration: 'none',
                color: '#1e293b'
              }}>
              
              <Phone style={{ width: 20, height: 20, color: '#d97706' }} aria-hidden="true" />
              <span style={{ fontSize: 11, fontWeight: 600 }}>(727) 228-6846</span>
            </a>
            <a
              href="sms:7276078699"
              onClick={closeMobile}
              aria-label="Text us at (727) 607-8699"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '14px 8px',
                gap: 4,
                background: '#f8fafc',
                borderRight: '1px solid #e2e8f0',
                textDecoration: 'none',
                color: '#1e293b'
              }}>
              
              <MessageSquare style={{ width: 20, height: 20, color: '#d97706' }} aria-hidden="true" />
              <span style={{ fontSize: 11, fontWeight: 600 }}>Text Us</span>
            </a>
            <button
              onClick={() => {
                base44.analytics.track({ eventName: 'book_appointment_clicked', properties: { location: 'mobile_menu' } });
                window.open('/book-appointment', '_blank');
                closeMobile();
              }}
              aria-label="Book an appointment online"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '14px 8px',
                gap: 4,
                background: '#d97706',
                border: 'none',
                cursor: 'pointer',
                color: '#fff'
              }}>
              
              <Calendar style={{ width: 20, height: 20 }} aria-hidden="true" />
              <span style={{ fontSize: 11, fontWeight: 600 }}>Book Now</span>
            </button>
          </div>

          {/* Scrollable nav — native scroll, no transforms */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'scroll',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              backgroundColor: '#fff'
            }}>
            
            {/* Key Services — front and center */}
            <div style={{ padding: '16px 20px 12px', backgroundColor: '#fffbeb', borderBottom: '2px solid #f1f5f9' }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#92400e', textTransform: 'uppercase', marginBottom: 12 }}>
                ⚡ Key Services
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                { label: 'Dental Implants', desc: 'Permanent tooth replacement', href: '/dental-implants', icon: '🦷' },
                { label: 'Signature Consult', desc: 'Reserve your $300 consult', href: '/book-appointment#signature-consult', icon: '✨' },
                { label: 'All-on-4® / All-on-6', desc: 'Full-arch restoration', href: '/all-on-x', icon: '😁' },
                { label: 'Cosmetic Dentistry', desc: 'Veneers & cosmetic care', href: '/cosmetic-dentistry', icon: '💎' },
                { label: 'Financing Options', desc: 'Subject to credit approval', href: '/financing-options', icon: '💳' },
                { label: 'Dental Insurance', desc: 'Why we\'re out of network', href: '/resources/dental-insurance', icon: '🛡️' },
                { label: 'Emergency Dentistry', desc: 'Same-day relief', href: '/emergency-dentistry', icon: '🚨' }].
                map((item) =>
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={closeMobile}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    padding: '12px 10px',
                    background: '#fff',
                    border: '1.5px solid #fde68a',
                    borderRadius: 10,
                    textDecoration: 'none',
                    minHeight: 44
                  }}>
                  
                    <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden="true">{item.icon}</span>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{item.label}</div>
                    <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.2 }}>{item.desc}</div>
                  </Link>
                )}
              </div>
            </div>

            {/* Full menu — rendered from the SAME navLinks source as the desktop nav, so every item appears */}
            <div style={{ padding: '4px 0' }}>
              {navLinks.map((item) =>
              item.subLinks ?
              <div key={item.href} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                      <Link
                    to={item.href}
                    onClick={closeMobile}
                    style={{ flex: 1, display: 'block', padding: '14px 20px', fontSize: 15, fontWeight: 700, color: '#1e293b', textDecoration: 'none', minHeight: 44 }}>
                    
                        {item.label}
                      </Link>
                      <button
                    onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                    aria-label={mobileExpanded === item.label ? `Collapse ${item.label} menu` : `Expand ${item.label} menu`}
                    aria-expanded={mobileExpanded === item.label}
                    style={{ padding: '0 20px', background: 'none', border: 'none', borderLeft: '1px solid #f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    
                        <ChevronDown style={{ width: 18, height: 18, color: '#d97706', transform: mobileExpanded === item.label ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                      </button>
                    </div>
                    {mobileExpanded === item.label &&
                <div style={{ background: '#f8fafc', paddingBottom: 4 }}>
                        {item.subLinks.map((sub) =>
                  <Link
                    key={sub.href}
                    to={sub.href}
                    onClick={closeMobile}
                    style={{ display: 'block', padding: '12px 20px 12px 32px', fontSize: 14, fontWeight: 500, color: '#475569', textDecoration: 'none', borderBottom: '1px solid #eef2f7', minHeight: 44 }}>
                    
                            {sub.label}
                          </Link>
                  )}
                      </div>
                }
                  </div> :

              <Link
                key={item.href}
                to={item.href}
                onClick={closeMobile}
                style={{ display: 'block', padding: '14px 20px', fontSize: 15, fontWeight: 700, color: '#1e293b', textDecoration: 'none', borderBottom: '1px solid #f1f5f9', minHeight: 44 }}>
                
                    {item.label}
                  </Link>

              )}
            </div>
            {/* bottom safe-area spacer */}
            <div style={{ height: 'env(safe-area-inset-bottom, 20px)' }} />
          </div>
        </div>,
        document.body
      )}
    </>);

}